import type {
  AbilityCategory,
  CompanionKind,
  HiddenEffectDefinition,
  HiddenRuntimeEffect,
  HiddenSynergyDefinition,
  ModuleDefinition,
  SpecialBehaviorId,
  SynergyAbilityModifier,
  SynergyDefinition,
  SynergyMechanicDefinition,
  SynergyRequirement,
  TargetMode,
  UndeadUnitAbilityDefinition,
  UndeadUnitDefinition,
} from "./phase-three-types";
import { isAllowedAttackIntervalMs } from "./attack-interval";
import {
  getModuleSkillRangeEntries,
  getSynergySkillRangeEntries,
  isAllowedSkillRange,
} from "./skill-range";
import {
  getModuleDpsErrors,
  getModulePaperDps,
} from "./skill-dps";
import { getUndeadUnit } from "./undead-units";
import {
  getFinanceStateTargetIds,
  validateFinanceImmediateTransitionCycles,
  validateFinanceModuleDefinition,
} from "./finance-state";
import { validateLaborDefinition } from "./labor";

export interface SkillOverrideDocument {
  version: 1;
  removedIds: string[];
  upserts: ModuleDefinition[];
}

export interface SkillOverrideValidation {
  document: SkillOverrideDocument;
  modules: ModuleDefinition[];
  errors: string[];
  warnings: string[];
}

export interface ContentCollectionOverride<T> {
  removedIds: string[];
  upserts: T[];
}

export interface WorkshopOverrideDocument {
  version: 2;
  skills: ContentCollectionOverride<ModuleDefinition>;
  synergies: ContentCollectionOverride<SynergyDefinition>;
  hiddenEffects: ContentCollectionOverride<HiddenEffectDefinition>;
  hiddenSynergies: ContentCollectionOverride<HiddenSynergyDefinition>;
  sharedUnits: ContentCollectionOverride<UndeadUnitDefinition>;
  archivedExploration: {
    skillEntries: unknown[];
    removedIds: string[];
  };
}

export interface WorkshopDefaultContent {
  modules: readonly ModuleDefinition[];
  synergies: readonly SynergyDefinition[];
  hiddenEffects: readonly HiddenEffectDefinition[];
  hiddenSynergies: readonly HiddenSynergyDefinition[];
  sharedUnits: readonly UndeadUnitDefinition[];
}

export interface WorkshopActiveContent {
  modules: ModuleDefinition[];
  synergies: SynergyDefinition[];
  hiddenEffects: HiddenEffectDefinition[];
  hiddenSynergies: HiddenSynergyDefinition[];
  sharedUnits: UndeadUnitDefinition[];
}

export interface SkillPreview {
  id: string;
  paperDps: number;
  ascensionRatio: number | null;
  evolutionId: string | null;
  conditionalEvolutionId: string | null;
  conditionalEvolutionIds: string[];
  conditionalEvolutionCount: number | null;
  synergyIds: string[];
  dependentSynergyIds: string[];
}

export interface SynergyPreview {
  id: string;
  uniqueSlots: number | null;
  reachable: boolean;
  dependencyIds: string[];
  dependentIds: string[];
}

export interface WorkshopPreview {
  tierCounts: Record<string, number>;
  categoryCounts: Record<string, number>;
  skillMetrics: Record<string, SkillPreview>;
  synergyMetrics: Record<string, SynergyPreview>;
}

export interface WorkshopDependency {
  evolutionIds: string[];
  synergyIds: string[];
  grantIds: string[];
  referencedByIds: string[];
}

export interface WorkshopValidation {
  document: WorkshopOverrideDocument;
  content: WorkshopActiveContent;
  errors: string[];
  warnings: string[];
  preview: WorkshopPreview;
  dependencies: {
    skills: Record<string, WorkshopDependency>;
    synergies: Record<string, WorkshopDependency>;
    hiddenEffects: Record<string, WorkshopDependency>;
    hiddenSynergies: Record<string, WorkshopDependency>;
    sharedUnits: Record<string, WorkshopDependency>;
  };
}

const ABILITY_CATEGORIES: readonly AbilityCategory[] = [
  "attack",
  "area",
  "elite_attack",
  "labor",
  "mining",
  "finance",
  "pasture",
  "enhancement",
  "follower",
  "summon",
  "healing",
  "growth",
  "one_shot",
  "guard",
  "special",
];

const TARGET_MODES: readonly TargetMode[] = [
  "random",
  "closest",
  "farthest",
  "highest_health",
  "lowest_health",
  "closest_to_core",
  "fastest",
  "alternating_extremes",
  "random_area",
  "elite_only",
  "boss_only",
];

const COMPANION_KINDS: readonly CompanionKind[] = [
  "pasture",
  "follower",
  "summon",
  "guard",
];

const SPECIAL_BEHAVIORS: readonly SpecialBehaviorId[] = [
  "pasture_inherits_attack",
  "portal_on_hit",
  "copy_random_ability",
  "emergency_overdrive",
  "companion_rebirth_burst",
  "all_chest_keys",
  "summon_echoes",
];

const ID_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

function getExperienceVaultTargetIds(module: ModuleDefinition): string[] {
  const evolution = module.experienceVault?.evolution;
  if (!evolution) {
    return [];
  }
  return evolution.kind === "fixed"
    ? [evolution.targetModuleId]
    : evolution.branches.map((branch) => branch.targetModuleId);
}

function getConditionalSkillTargetIds(module: ModuleDefinition): string[] {
  return [
    module.conditionalEvolution?.targetModuleId,
    module.killCharge?.targetModuleId,
    ...getExperienceVaultTargetIds(module),
  ].filter((id): id is string => Boolean(id));
}

function getReferencedSkillTargetIds(module: ModuleDefinition): string[] {
  return [
    ...getConditionalSkillTargetIds(module),
    ...getFinanceStateTargetIds(module),
  ];
}

function isActiveSurfaceMiningModule(input: unknown): boolean {
  const module = asRecord(input);
  const surfaceMining = asRecord(module?.surfaceMining);
  return Boolean(
      module?.id !== "mining_pickaxe" &&
      module?.category === "mining" &&
      (surfaceMining?.schemaVersion === 1 ||
        surfaceMining?.schemaVersion === 2),
  );
}

function hasArchivedExplorationFields(input: unknown): boolean {
  const module = asRecord(input);
  const bonuses = asRecord(module?.bonuses);
  const activeSurfaceMining = isActiveSurfaceMiningModule(input);
  return Boolean(
    module?.id === "mining_pickaxe" ||
      (module?.category === "mining" && !activeSurfaceMining) ||
      module?.category === "archaeology" ||
      module?.mining !== undefined ||
      module?.archaeology !== undefined ||
      (Array.isArray(module?.tags) &&
        module.tags.some(
          (tag) =>
            tag === "archaeology" ||
            (tag === "mining" && !activeSurfaceMining),
        )) ||
      bonuses?.surveyRadiusAdd !== undefined ||
      bonuses?.miningWorkRateAdd !== undefined ||
      bonuses?.miningWorkRateMultiplier !== undefined ||
      bonuses?.carryCapacityAdd !== undefined ||
      bonuses?.archaeologyMultiplier !== undefined ||
      bonuses?.wageShareAdd !== undefined ||
      asRecord(module?.economy)?.trigger === "mining_work" ||
      asRecord(module?.economy)?.trigger === "excavate" ||
      asRecord(module?.growth)?.event === "mining_work" ||
      asRecord(module?.growth)?.event === "excavate",
  );
}

/**
 * Compatibility boundary for protocol-17/18 workshop files. Exploration-only
 * skills are removed from active content, while mixed skills keep their unrelated
 * fields and lose only archaeology/mining fragments.
 */
function sanitizeArchivedExplorationModule(
  input: unknown,
): ModuleDefinition | null {
  const module = cloneJson(input) as ModuleDefinition;
  const editable = module as ModuleDefinition & Record<string, unknown>;
  const rawCategory = (editable as Record<string, unknown>).category;
  const activeSurfaceMining = isActiveSurfaceMiningModule(input);
  if (
    editable.id === "mining_pickaxe" ||
    (rawCategory === "mining" && !activeSurfaceMining) ||
    rawCategory === "archaeology"
  ) {
    return null;
  }
  if (Array.isArray(editable.tags)) {
    editable.tags = editable.tags.filter(
      (tag) =>
        tag !== "archaeology" && (activeSurfaceMining || tag !== "mining"),
    );
  }

  const bonuses = asRecord(editable.bonuses);
  if (bonuses) {
    delete bonuses.surveyRadiusAdd;
    delete bonuses.miningWorkRateAdd;
    delete bonuses.miningWorkRateMultiplier;
    delete bonuses.carryCapacityAdd;
    delete bonuses.archaeologyMultiplier;
    delete bonuses.wageShareAdd;
  }

  delete editable.mining;
  delete editable.archaeology;

  const economy = asRecord(editable.economy);
  if (economy?.trigger === "excavate" || economy?.trigger === "mining_work") {
    delete editable.economy;
  }
  const growth = asRecord(editable.growth);
  if (growth?.event === "excavate" || growth?.event === "mining_work") {
    delete editable.growth;
  }
  return module;
}

export function validateSkillOverrideDocument(
  defaultModules: readonly ModuleDefinition[],
  input: unknown,
  sharedUnits?: readonly UndeadUnitDefinition[],
): SkillOverrideValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const object = asRecord(input);
  if (!object) {
    return {
      document: emptySkillOverrideDocument(),
      modules: [...defaultModules],
      errors: ["技能覆盖配置必须是 JSON 对象。"],
      warnings,
    };
  }
  if (object.version !== 1) {
    errors.push("技能覆盖配置 version 必须为 1。");
  }

  const rawRemovedIds = Array.isArray(object.removedIds)
    ? object.removedIds
    : [];
  if (!Array.isArray(object.removedIds)) {
    errors.push("removedIds 必须是字符串数组。");
  }
  const removedIds = rawRemovedIds.flatMap((value, index) => {
    if (typeof value !== "string" || !ID_PATTERN.test(value)) {
      errors.push(`removedIds[${index}] 不是有效技能ID。`);
      return [];
    }
    return [value];
  });

  const rawUpserts = Array.isArray(object.upserts) ? object.upserts : [];
  if (!Array.isArray(object.upserts)) {
    errors.push("upserts 必须是技能数组。");
  }
  const upserts: ModuleDefinition[] = [];
  const upsertIds = new Set<string>();
  for (const [index, value] of rawUpserts.entries()) {
    if (hasArchivedExplorationFields(value)) {
      warnings.push(
        `upserts[${index}] 含已封存的考古/采矿内容：专属技能不会进入活动池，混合技能只保留无关字段。`,
      );
    }
    const module = sanitizeArchivedExplorationModule(value);
    if (!module) {
      continue;
    }
    const moduleErrors = validateEditableModule(module, sharedUnits);
    errors.push(
      ...moduleErrors.map((message) => `upserts[${index}]：${message}`),
    );
    if (
      typeof module?.id === "string" &&
      ID_PATTERN.test(module.id)
    ) {
      if (upsertIds.has(module.id)) {
        errors.push(`upserts 中存在重复技能ID：${module.id}。`);
      } else {
        upsertIds.add(module.id);
        upserts.push(module);
      }
    }
  }

  const knownIds = new Set(defaultModules.map((module) => module.id));
  for (const id of removedIds) {
    if (!knownIds.has(id) && !upsertIds.has(id)) {
      warnings.push(`待删除技能 ${id} 不在当前默认技能池中。`);
    }
  }

  const relationshipMap = new Map(
    [...defaultModules, ...upserts].map((module) => [module.id, module]),
  );
  const cascadedRemovedIds = new Set(
    removedIds.filter((id) => !upsertIds.has(id)),
  );
  const removalQueue = [...cascadedRemovedIds];
  while (removalQueue.length > 0) {
    const id = removalQueue.shift()!;
    const module = relationshipMap.get(id);
    const relatedIds = [
      module?.evolutionTo,
      module?.evolutionFrom,
      ...(module ? getReferencedSkillTargetIds(module) : []),
      ...[...relationshipMap.values()].flatMap((candidate) =>
        getReferencedSkillTargetIds(candidate).includes(id)
          ? [candidate.id]
          : [],
      ),
    ].filter((value): value is string => Boolean(value));
    for (const relatedId of relatedIds) {
      if (
        !upsertIds.has(relatedId) &&
        !cascadedRemovedIds.has(relatedId)
      ) {
        cascadedRemovedIds.add(relatedId);
        removalQueue.push(relatedId);
        warnings.push(
          `删除 ${id} 时同步删除其固定升格关系技能 ${relatedId}。`,
        );
      }
    }
  }

  const upsertById = new Map(upserts.map((module) => [module.id, module]));
  const modules = defaultModules.flatMap((module) => {
    if (cascadedRemovedIds.has(module.id)) {
      return [];
    }
    return [cloneJson(upsertById.get(module.id) ?? module)];
  });
  for (const module of upserts) {
    if (!knownIds.has(module.id) && !cascadedRemovedIds.has(module.id)) {
      modules.push(cloneJson(module));
    }
  }

  const resultIds = new Set(modules.map((module) => module.id));
  if (resultIds.size !== modules.length) {
    errors.push("应用覆盖配置后的技能ID不唯一。");
  }
  const moduleById = new Map(modules.map((module) => [module.id, module]));
  for (const module of modules) {
    errors.push(
      ...validateFinanceModuleDefinition(module, moduleById).map(
        (message) => `技能 ${module.id} 的理财状态无效：${message}。`,
      ),
    );
    errors.push(
      ...validateLaborDefinition(module).map(
        (message) => `技能 ${module.id} 的打工状态无效：${message}。`,
      ),
    );
  }
  errors.push(...validateFinanceImmediateTransitionCycles(modules));
  const conditionalSourcesByTarget = new Map<string, ModuleDefinition[]>();
  for (const source of modules) {
    const targetId = source.conditionalEvolution?.targetModuleId;
    if (!targetId) {
      continue;
    }
    const sources = conditionalSourcesByTarget.get(targetId) ?? [];
    sources.push(source);
    conditionalSourcesByTarget.set(targetId, sources);
  }
  for (const source of modules) {
    const evolution = source.conditionalEvolution;
    if (!evolution) {
      continue;
    }
    const target = modules.find((module) => module.id === evolution.targetModuleId);
    const validSurfaceEvolution =
      evolution.kind === "surface_mining_devour_count" &&
      source.tier === 3 &&
      source.hidden &&
      source.surfaceMining?.devour &&
      target?.tier === 6 &&
      target.hidden &&
      target.surfaceMining?.contactAttack &&
      !target.evolutionFrom &&
      (conditionalSourcesByTarget.get(target.id)?.length ?? 0) === 1;
    const validLevelEvolution =
      evolution.kind === "player_level" &&
      source.hidden &&
      source.weight === 0 &&
      target?.tier === source.tier + 1 &&
      target.hidden &&
      target.weight === 0 &&
      !target.evolutionFrom;
    if (!validSurfaceEvolution && !validLevelEvolution) {
      errors.push(`技能 ${source.id} 的条件进阶映射无效。`);
    }
  }
  for (const source of modules) {
    if (source.experienceVault) {
      const vault = source.experienceVault;
      const targets = getExperienceVaultTargetIds(source);
      const weightedValid =
        vault.evolution.kind !== "weighted" ||
        (vault.evolution.branches.length >= 2 &&
          new Set(targets).size === targets.length &&
          vault.evolution.branches.every(
            (branch) => Number.isFinite(branch.weight) && branch.weight > 0,
          ) &&
          Math.abs(
            vault.evolution.branches.reduce(
              (sum, branch) => sum + branch.weight,
              0,
            ) - 100,
          ) < 1e-9);
      const targetsValid = targets.every((targetId) => {
        const target = modules.find((module) => module.id === targetId);
        return Boolean(
          target &&
          target.tier === source.tier + 1 &&
          target.hidden &&
          target.weight === 0,
        );
      });
      if (
        !Number.isFinite(vault.capacity) ||
        vault.capacity <= 0 ||
        !Number.isFinite(vault.replacementPayoutMultiplier) ||
        vault.replacementPayoutMultiplier < 0 ||
        vault.suppressPayoutOnEvolution !== true ||
        targets.length === 0 ||
        !weightedValid ||
        !targetsValid
      ) {
        errors.push(`技能 ${source.id} 的经验仓或概率进阶配置无效。`);
      }
    }
    if (source.killCharge) {
      const charge = source.killCharge;
      const target = modules.find(
        (module) => module.id === charge.targetModuleId,
      );
      if (
        charge.trigger !== "direct_kill" ||
        !Number.isInteger(charge.requiredCount) ||
        charge.requiredCount < 1 ||
        charge.sameSlot !== true ||
        !target ||
        target.tier !== source.tier + 1 ||
        !target.hidden ||
        target.weight !== 0
      ) {
        errors.push(`技能 ${source.id} 的逐槽击杀进阶配置无效。`);
      }
    }
  }
  const conditionalGraph = new Map(
    modules.map((module) => [
      module.id,
      [
        module.evolutionTo,
        ...getConditionalSkillTargetIds(module),
      ].filter((id): id is string => Boolean(id)),
    ]),
  );
  for (const module of modules) {
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const hasCycle = (id: string): boolean => {
      if (visiting.has(id)) return true;
      if (visited.has(id)) return false;
      visiting.add(id);
      for (const targetId of conditionalGraph.get(id) ?? []) {
        if (hasCycle(targetId)) return true;
      }
      visiting.delete(id);
      visited.add(id);
      return false;
    };
    if (hasCycle(module.id)) {
      errors.push(`技能 ${module.id} 的条件进阶关系存在循环。`);
    }
  }
  for (const module of modules) {
    if (module.tier === 5) {
      const stateCanonical = module.stateFamily?.inheritsAscension
        ? moduleById.get(module.stateFamily.canonicalModuleId)
        : undefined;
      const ascended = module.evolutionTo
        ? modules.find((candidate) => candidate.id === module.evolutionTo)
        : undefined;
      if (
        !stateCanonical &&
        (!ascended ||
          ascended.tier !== 6 ||
          ascended.evolutionFrom !== module.id)
      ) {
        errors.push(
          `五阶技能 ${module.id} 必须指向一个互相对应的六阶技能。`,
        );
      }
    }
    if (module.tier === 6) {
      const stateCanonical = module.stateFamily?.inheritsSlotLock
        ? moduleById.get(module.stateFamily.canonicalModuleId)
        : undefined;
      const conditionalSources = conditionalSourcesByTarget.get(module.id) ?? [];
      const origin = module.evolutionFrom
        ? modules.find((candidate) => candidate.id === module.evolutionFrom)
        : undefined;
      const stagedOrigin = Boolean(
        origin?.tags.includes("multi_stage_ascension") &&
          module.tags.includes("multi_stage_ascension") &&
          origin.stagedAscension?.targetModuleId === module.id,
      );
      if (
        conditionalSources.length === 0 &&
        !stateCanonical &&
        !stagedOrigin &&
        (!origin || origin.tier !== 5 || origin.evolutionTo !== module.id)
      ) {
        errors.push(
          `六阶技能 ${module.id} 必须指向一个互相对应的五阶技能。`,
        );
      }
      if (
        conditionalSources.length > 0 &&
        (conditionalSources.length !== 1 ||
          Boolean(module.evolutionFrom) ||
          !module.hidden ||
          !module.surfaceMining?.contactAttack)
      ) {
        errors.push(`六阶技能 ${module.id} 的条件进阶来源无效。`);
      }
      if (module.weight !== 0) {
        errors.push(`六阶技能 ${module.id} 的随机权重必须为 0。`);
      }
    }
    errors.push(
      ...getModuleDpsErrors(module, modules, sharedUnits).map(
        (message) => `技能 ${module.id}：${message}`,
      ),
    );
  }
  for (const tier of [1, 2, 3, 4, 5] as const) {
    const available = modules.filter(
      (module) => module.tier === tier && module.weight > 0,
    ).length;
    if (available < 4) {
      const defaultAvailable = defaultModules.filter(
        (module) => module.tier === tier && module.weight > 0,
      ).length;
      const message =
        `${tier}阶至少需要4个可随机技能，才能保证放弃并重铸后的三选一必定刷新。`;
      if (defaultAvailable >= 4) {
        errors.push(message);
      } else {
        warnings.push(`P4重建中：${message}`);
      }
    }
  }

  return {
    document: {
      version: 1,
      removedIds: [...cascadedRemovedIds].sort(),
      upserts: upserts.sort(
        (a, b) => a.tier - b.tier || a.id.localeCompare(b.id),
      ),
    },
    modules,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
  };
}

export function applySkillOverrides(
  defaultModules: readonly ModuleDefinition[],
  input: unknown,
): ModuleDefinition[] {
  const object = asRecord(input);
  const source =
    object?.version === 2
      ? {
          version: 1,
          ...readCollectionOverride<ModuleDefinition>(object.skills),
        }
      : input;
  const validation = validateSkillOverrideDocument(defaultModules, source);
  if (validation.errors.length > 0) {
    throw new Error(
      `技能覆盖配置无效：\n${validation.errors
        .map((message) => `- ${message}`)
        .join("\n")}`,
    );
  }
  return validation.modules;
}

export function adaptSynergiesToModules(
  synergies: readonly SynergyDefinition[],
  modules: readonly ModuleDefinition[],
): SynergyDefinition[] {
  const moduleIds = new Set(modules.map((module) => module.id));
  let adapted = synergies.flatMap((synergy) => {
    const requirements: SynergyRequirement[] = [];
    let lostRequiredModule = false;
    for (const requirement of synergy.requirements) {
      if (requirement.kind === "module") {
        if (!moduleIds.has(requirement.moduleId)) {
          lostRequiredModule = true;
        } else {
          requirements.push(requirement);
        }
        continue;
      }
      if (requirement.kind === "one_of") {
        const remainingIds = requirement.moduleIds.filter((id) =>
          moduleIds.has(id),
        );
        if (remainingIds.length === 0) {
          lostRequiredModule = true;
        } else {
          requirements.push({ ...requirement, moduleIds: remainingIds });
        }
        continue;
      }
      requirements.push(requirement);
    }
    if (lostRequiredModule) {
      return [];
    }
    const abilityModifiers = synergy.abilityModifiers
      ?.map((modifier) => ({
        ...modifier,
        moduleIds: modifier.moduleIds.filter((id) => moduleIds.has(id)),
      }))
      .filter((modifier) => modifier.moduleIds.length > 0);
    return [
      {
        ...synergy,
        requirements,
        abilityModifiers,
      },
    ];
  });
  let changed = true;
  while (changed) {
    const activeIds = new Set(adapted.map((synergy) => synergy.id));
    const next = adapted.filter((synergy) =>
      synergy.requirements.every(
        (requirement) =>
          requirement.kind !== "synergy" ||
          activeIds.has(requirement.synergyId),
      ),
    );
    changed = next.length !== adapted.length;
    adapted = next;
  }
  return adapted;
}

export function emptySkillOverrideDocument(): SkillOverrideDocument {
  return {
    version: 1,
    removedIds: [],
    upserts: [],
  };
}

export function emptyWorkshopOverrideDocument(): WorkshopOverrideDocument {
  return {
    version: 2,
    skills: emptyCollectionOverride(),
    synergies: emptyCollectionOverride(),
    hiddenEffects: emptyCollectionOverride(),
    hiddenSynergies: emptyCollectionOverride(),
    sharedUnits: emptyCollectionOverride(),
    archivedExploration: {
      skillEntries: [],
      removedIds: [],
    },
  };
}

function migrateExplorationOverrides(
  rawUpserts: unknown[],
  rawRemovedIds: unknown[],
  existingArchive?: unknown,
): {
  upserts: ModuleDefinition[];
  removedIds: string[];
  archive: WorkshopOverrideDocument["archivedExploration"];
} {
  const existing = asRecord(existingArchive);
  const existingEntries = Array.isArray(existing?.skillEntries)
    ? existing.skillEntries.map(cloneJson)
    : [];
  const existingRemovedIds = Array.isArray(existing?.removedIds)
    ? existing.removedIds.filter((id): id is string => typeof id === "string")
    : [];
  const archivedEntries = [
    ...existingEntries,
    ...rawUpserts.filter(hasArchivedExplorationFields).map(cloneJson),
  ];
  const wholeArchivedIds = new Set(
    rawUpserts.flatMap((entry) => {
      const record = asRecord(entry);
      const sanitized = sanitizeArchivedExplorationModule(entry);
      return !sanitized && typeof record?.id === "string" ? [record.id] : [];
    }),
  );
  const removedIds = rawRemovedIds.filter(
    (id): id is string =>
      typeof id === "string" &&
      id !== "mining_pickaxe" &&
      !wholeArchivedIds.has(id),
  );
  const archivedRemovedIds = rawRemovedIds.filter(
    (id): id is string =>
      typeof id === "string" &&
      (id === "mining_pickaxe" || wholeArchivedIds.has(id)),
  );
  const uniqueEntries = new Map(
    archivedEntries.map((entry) => [JSON.stringify(entry), entry]),
  );
  return {
    upserts: rawUpserts.flatMap((entry) => {
      const migrated = sanitizeArchivedExplorationModule(entry);
      return migrated ? [migrated] : [];
    }),
    removedIds,
    archive: {
      skillEntries: [...uniqueEntries.values()],
      removedIds: [...new Set([...existingRemovedIds, ...archivedRemovedIds])],
    },
  };
}

export function migrateWorkshopOverrideDocument(
  input: unknown,
): WorkshopOverrideDocument {
  const object = asRecord(input);
  if (object?.version === 1) {
    const migrated = migrateExplorationOverrides(
      Array.isArray(object.upserts) ? object.upserts : [],
      Array.isArray(object.removedIds) ? object.removedIds : [],
    );
    return {
      ...emptyWorkshopOverrideDocument(),
      skills: {
        removedIds: migrated.removedIds,
        upserts: migrated.upserts,
      },
      archivedExploration: migrated.archive,
    };
  }
  if (object?.version !== 2) {
    return emptyWorkshopOverrideDocument();
  }
  const rawSkills = readCollectionOverride<unknown>(object.skills);
  const migrated = migrateExplorationOverrides(
    rawSkills.upserts,
    rawSkills.removedIds,
    object.archivedExploration,
  );
  return {
    version: 2,
    skills: {
      removedIds: migrated.removedIds,
      upserts: migrated.upserts,
    },
    synergies: readCollectionOverride<SynergyDefinition>(object.synergies),
    hiddenEffects: readCollectionOverride<HiddenEffectDefinition>(
      object.hiddenEffects,
    ),
    hiddenSynergies: readCollectionOverride<HiddenSynergyDefinition>(
      object.hiddenSynergies,
    ),
    sharedUnits: readCollectionOverride<UndeadUnitDefinition>(
      object.sharedUnits,
    ),
    archivedExploration: migrated.archive,
  };
}

export function applyContentCollectionOverrides<T extends { id: string }>(
  defaults: readonly T[],
  patch: ContentCollectionOverride<T>,
): T[] {
  const removed = new Set(patch.removedIds);
  const upserts = new Map(
    patch.upserts.map((entry) => [entry.id, cloneJson(entry)]),
  );
  const result = defaults.flatMap((entry) =>
    removed.has(entry.id)
      ? []
      : [cloneJson(upserts.get(entry.id) ?? entry)],
  );
  const defaultIds = new Set(defaults.map((entry) => entry.id));
  for (const entry of patch.upserts) {
    if (!defaultIds.has(entry.id) && !removed.has(entry.id)) {
      result.push(cloneJson(entry));
    }
  }
  return result;
}

export function applySynergyOverrides(
  defaults: readonly SynergyDefinition[],
  input: unknown,
): SynergyDefinition[] {
  const document = migrateWorkshopOverrideDocument(input);
  return applyContentCollectionOverrides(defaults, document.synergies);
}

export function applyHiddenEffectOverrides(
  defaults: readonly HiddenEffectDefinition[],
  input: unknown,
): HiddenEffectDefinition[] {
  const document = migrateWorkshopOverrideDocument(input);
  return applyContentCollectionOverrides(defaults, document.hiddenEffects);
}

export function applyHiddenSynergyOverrides(
  defaults: readonly HiddenSynergyDefinition[],
  input: unknown,
): HiddenSynergyDefinition[] {
  const document = migrateWorkshopOverrideDocument(input);
  return applyContentCollectionOverrides(defaults, document.hiddenSynergies);
}

export function applySharedUnitOverrides(
  defaults: readonly UndeadUnitDefinition[],
  input: unknown,
): UndeadUnitDefinition[] {
  const document = migrateWorkshopOverrideDocument(input);
  return applyContentCollectionOverrides(defaults, document.sharedUnits);
}

export function validateWorkshopOverrideDocument(
  defaults: WorkshopDefaultContent,
  input: unknown,
): WorkshopValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const object = asRecord(input);
  if (!object) {
    errors.push("工坊覆盖配置必须是 JSON 对象。");
  } else if (object.version !== 1 && object.version !== 2) {
    errors.push("工坊覆盖配置 version 必须为 1 或 2。");
  }
  const migrated = migrateWorkshopOverrideDocument(input);
  const document: WorkshopOverrideDocument = {
    version: 2,
    skills: normalizeCollectionOverride(
      migrated.skills,
      "skills",
      errors,
    ),
    synergies: normalizeCollectionOverride(
      migrated.synergies,
      "synergies",
      errors,
    ),
    hiddenEffects: normalizeCollectionOverride(
      migrated.hiddenEffects,
      "hiddenEffects",
      errors,
    ),
    hiddenSynergies: normalizeCollectionOverride(
      migrated.hiddenSynergies,
      "hiddenSynergies",
      errors,
    ),
    sharedUnits: normalizeCollectionOverride(
      migrated.sharedUnits,
      "sharedUnits",
      errors,
    ),
    archivedExploration: {
      skillEntries: migrated.archivedExploration.skillEntries.map(cloneJson),
      removedIds: [...migrated.archivedExploration.removedIds],
    },
  };
  if (object?.version === 1) {
    warnings.push("已兼容读取旧版技能覆盖；下次保存会迁移为统一覆盖格式 v2。");
  }
  if (
    document.archivedExploration.skillEntries.length > 0 ||
    document.archivedExploration.removedIds.length > 0
  ) {
    warnings.push(
      `已只读封存 ${document.archivedExploration.skillEntries.length} 条考古/采矿技能覆盖和 ${document.archivedExploration.removedIds.length} 条删除记录；它们不会进入活动内容。`,
    );
  }

  const sharedUnits = applyContentCollectionOverrides(
    defaults.sharedUnits,
    document.sharedUnits,
  );
  errors.push(...validateSharedUnits(sharedUnits));

  const skillValidation = validateSkillOverrideDocument(
    defaults.modules,
    {
      version: 1,
      removedIds: document.skills.removedIds,
      upserts: document.skills.upserts,
    },
    sharedUnits,
  );
  document.skills = {
    removedIds: skillValidation.document.removedIds,
    upserts: skillValidation.document.upserts,
  };
  errors.push(...skillValidation.errors);
  warnings.push(...skillValidation.warnings);
  const modules = skillValidation.modules;

  const synergies = applyContentCollectionOverrides(
    defaults.synergies,
    document.synergies,
  );
  const hiddenEffects = applyContentCollectionOverrides(
    defaults.hiddenEffects,
    document.hiddenEffects,
  );
  const hiddenSynergies = applyContentCollectionOverrides(
    defaults.hiddenSynergies,
    document.hiddenSynergies,
  );

  errors.push(
    ...validateHiddenContent(hiddenEffects, hiddenSynergies),
    ...validateSynergyContent(
      synergies,
      modules,
      hiddenEffects,
      sharedUnits,
    ),
  );
  const hiddenEffectIds = new Set(hiddenEffects.map((effect) => effect.id));
  const moduleById = new Map(modules.map((module) => [module.id, module]));
  for (const module of modules) {
    errors.push(
      ...validateFinanceModuleDefinition(
        module,
        moduleById,
        hiddenEffectIds,
      ).map((message) => `技能 ${module.id} 的理财状态无效：${message}。`),
    );
    errors.push(
      ...validateLaborDefinition(module, hiddenEffectIds).map(
        (message) => `技能 ${module.id} 的打工状态无效：${message}。`,
      ),
    );
    for (const effectId of module.grantsHiddenEffectIds ?? []) {
      if (!hiddenEffectIds.has(effectId)) {
        errors.push(
          `技能 ${module.id} 授予了不存在的隐性效果 ${effectId}。`,
        );
      }
    }
    const repeatableEffectId = module.onAcquire?.repeatableHiddenEffectId;
    if (repeatableEffectId) {
      const effect = hiddenEffects.find(
        (candidate) => candidate.id === repeatableEffectId,
      );
      if (!effect?.repeatable) {
        errors.push(
          `技能 ${module.id} 的单次效果引用了不存在或不可重复的隐性效果 ${repeatableEffectId}。`,
        );
      }
    }
    const financeEffectIds = [
      module.financeState?.onEnterGrant?.repeatableHiddenEffectId,
      module.financeState?.timedOutcome?.kind === "stake_resolution"
        ? module.financeState.timedOutcome.successGrant
            ?.repeatableHiddenEffectId
        : undefined,
      module.financeState?.timedOutcome?.kind === "stake_resolution"
        ? module.financeState.timedOutcome.dynamicSuccess
            ?.repeatableHiddenEffectId
        : undefined,
    ].filter((id): id is string => Boolean(id));
    for (const effectId of financeEffectIds) {
      if (!hiddenEffects.find((effect) => effect.id === effectId)?.repeatable) {
        errors.push(
          `技能 ${module.id} 的理财状态引用了不存在或不可重复的隐性效果 ${effectId}。`,
        );
      }
    }
  }

  const cycleIds = getSynergyCycleIds(synergies);
  for (const cycleId of cycleIds) {
    errors.push(`羁绊依赖存在循环：${cycleId}。`);
  }

  const slotOptionsById = new Map<string, ReadonlySet<string>[]>();
  const synergyById = new Map(synergies.map((synergy) => [synergy.id, synergy]));
  for (const synergy of synergies) {
    if (cycleIds.has(synergy.id)) {
      continue;
    }
    const options = resolveSynergySlotOptions(
      synergy.id,
      synergyById,
      moduleById,
      slotOptionsById,
      new Set(),
    );
    if (options.length === 0) {
      errors.push(`羁绊 ${synergy.id} 的前置条件无法在当前技能池中达成。`);
    } else {
      const minimum = Math.min(...options.map((option) => option.size));
      if (minimum > 6) {
        errors.push(
          `羁绊 ${synergy.id} 至少需要 ${minimum} 个唯一技能槽，超过六槽上限。`,
        );
      }
    }
  }

  const content: WorkshopActiveContent = {
    modules,
    synergies,
    hiddenEffects,
    hiddenSynergies,
    sharedUnits,
  };
  return {
    document,
    content,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
    preview: buildWorkshopPreview(content, slotOptionsById),
    dependencies: collectWorkshopDependencies(content),
  };
}

function emptyCollectionOverride<T>(): ContentCollectionOverride<T> {
  return { removedIds: [], upserts: [] };
}

function readCollectionOverride<T>(
  input: unknown,
): ContentCollectionOverride<T> {
  const object = asRecord(input);
  return {
    removedIds: Array.isArray(object?.removedIds)
      ? cloneJson(object.removedIds) as string[]
      : [],
    upserts: Array.isArray(object?.upserts)
      ? cloneJson(object.upserts) as T[]
      : [],
  };
}

function normalizeCollectionOverride<T extends { id: string }>(
  input: ContentCollectionOverride<T>,
  label: string,
  errors: string[],
): ContentCollectionOverride<T> {
  const removedIds: string[] = [];
  const removedSeen = new Set<string>();
  for (const [index, id] of input.removedIds.entries()) {
    if (typeof id !== "string" || !ID_PATTERN.test(id)) {
      errors.push(`${label}.removedIds[${index}] 不是有效ID。`);
    } else if (!removedSeen.has(id)) {
      removedSeen.add(id);
      removedIds.push(id);
    }
  }
  const upserts: T[] = [];
  const upsertSeen = new Set<string>();
  for (const [index, entry] of input.upserts.entries()) {
    const object = asRecord(entry);
    const id = object?.id;
    if (typeof id !== "string" || !ID_PATTERN.test(id)) {
      errors.push(`${label}.upserts[${index}] 缺少有效ID。`);
    } else if (upsertSeen.has(id)) {
      errors.push(`${label}.upserts 中存在重复ID：${id}。`);
    } else {
      upsertSeen.add(id);
      upserts.push(cloneJson(entry));
    }
  }
  return {
    removedIds: removedIds.sort(),
    upserts: upserts.sort((a, b) => a.id.localeCompare(b.id)),
  };
}

function validateSharedUnits(
  units: readonly UndeadUnitDefinition[],
): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const abilityKinds = new Set<UndeadUnitAbilityDefinition["kind"]>([
    "taunt",
    "cone_splash",
    "charge",
    "kill_heal",
    "periodic_area_damage",
    "kill_growth",
  ]);
  for (const unit of units) {
    const prefix = `共享单位 ${unit.id}`;
    if (!ID_PATTERN.test(unit.id)) {
      errors.push(`${prefix} 的ID无效。`);
    }
    if (ids.has(unit.id)) {
      errors.push(`共享单位ID重复：${unit.id}。`);
    }
    ids.add(unit.id);
    if (
      !unit.name?.trim() ||
      !unit.presentationId?.trim() ||
      !positive(unit.health) ||
      !nonNegative(unit.armor) ||
      !nonNegative(unit.damage) ||
      !isAllowedAttackIntervalMs(unit.intervalMs) ||
      !isAllowedSkillRange(unit.range) ||
      !positive(unit.moveSpeedBaseRatio) ||
      !Array.isArray(unit.abilities)
    ) {
      errors.push(`${prefix} 的基础属性、模型接口、攻击间隔或范围无效。`);
    }
    for (const ability of unit.abilities ?? []) {
      if (!abilityKinds.has(ability.kind)) {
        errors.push(`${prefix} 包含未知单位机制 ${(ability as { kind?: unknown }).kind}。`);
        continue;
      }
      if (!hasOnlyFiniteNumbers(ability)) {
        errors.push(`${prefix} 的 ${ability.kind} 包含非法数值。`);
      }
      if (
        ability.kind === "cone_splash" &&
        (!positive(ability.angleDegrees) ||
          ability.angleDegrees > 360 ||
          !isAllowedSkillRange(ability.radius) ||
          !nonNegative(ability.damageMultiplier))
      ) {
        errors.push(`${prefix} 的锥形溅射配置无效。`);
      }
      if (
        ability.kind === "charge" &&
        (!isAllowedAttackIntervalMs(ability.intervalMs) ||
          !isAllowedSkillRange(ability.acquireRange) ||
          !isAllowedSkillRange(ability.pathWidth) ||
          !nonNegative(ability.damage) ||
          !nonNegative(ability.stunDurationMs))
      ) {
        errors.push(`${prefix} 的冲锋配置无效。`);
      }
      if (
        ability.kind === "periodic_area_damage" &&
        (!isAllowedAttackIntervalMs(ability.intervalMs) ||
          !isAllowedSkillRange(ability.radius) ||
          !nonNegative(ability.damage))
      ) {
        errors.push(`${prefix} 的周期范围伤害配置无效。`);
      }
      if (
        ability.kind === "kill_heal" &&
        !nonNegative(ability.healing)
      ) {
        errors.push(`${prefix} 的击杀治疗配置无效。`);
      }
      if (
        ability.kind === "kill_growth" &&
        (!nonNegative(ability.maxHealthAdd) ||
          !nonNegative(ability.currentHealthAdd) ||
          !nonNegative(ability.damageAdd))
      ) {
        errors.push(`${prefix} 的击杀成长配置无效。`);
      }
    }
  }
  return errors;
}

function validateHiddenContent(
  effects: readonly HiddenEffectDefinition[],
  synergies: readonly HiddenSynergyDefinition[],
): string[] {
  const errors: string[] = [];
  const effectIds = new Set<string>();
  const synergyIds = new Set<string>();
  for (const effect of effects) {
    if (!ID_PATTERN.test(effect.id) || !effect.name?.trim() || !effect.description?.trim()) {
      errors.push(`隐性效果 ${effect.id ?? "(无ID)"} 的ID、名称或说明无效。`);
    }
    if (effectIds.has(effect.id)) {
      errors.push(`隐性效果ID重复：${effect.id}。`);
    }
    effectIds.add(effect.id);
    if (typeof effect.startsLocked !== "boolean") {
      errors.push(`隐性效果 ${effect.id} 的初始锁定标记无效。`);
    }
    if (effect.repeatable && effect.startsLocked) {
      errors.push(`可重复隐性效果 ${effect.id} 不能使用锁定状态。`);
    }
    if (
      effect.repeatable &&
      typeof effect.repeatable.trackSourceTier !== "boolean"
    ) {
      errors.push(`可重复隐性效果 ${effect.id} 的来源品阶记录标记无效。`);
    }
    errors.push(...validateHiddenRuntimeEffects(effect.effects, `隐性效果 ${effect.id}`));
  }
  for (const synergy of synergies) {
    if (!ID_PATTERN.test(synergy.id) || !synergy.name?.trim() || !synergy.description?.trim()) {
      errors.push(`隐性羁绊 ${synergy.id ?? "(无ID)"} 的ID、名称或说明无效。`);
    }
    if (synergyIds.has(synergy.id)) {
      errors.push(`隐性羁绊ID重复：${synergy.id}。`);
    }
    synergyIds.add(synergy.id);
    for (const effectId of [
      ...(synergy.requiredEffectIds ?? []),
      ...(synergy.activatesEffectIds ?? []),
    ]) {
      if (!effectIds.has(effectId)) {
        errors.push(`隐性羁绊 ${synergy.id} 引用了未知隐性效果 ${effectId}。`);
      }
    }
    const repeatable = synergy.repeatableRequirement;
    if (repeatable) {
      const source = effects.find((effect) => effect.id === repeatable.effectId);
      if (!source?.repeatable) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 必须引用可重复隐性效果。`);
      }
      if (!Number.isInteger(repeatable.count) || repeatable.count < 1) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 的阈值必须为正整数。`);
      }
      if (repeatable.minSourceTier && repeatable.exactSourceTier) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 不能同时设置最低和指定来源品阶。`);
      }
      for (const tier of [repeatable.minSourceTier, repeatable.exactSourceTier]) {
        if (tier !== undefined && (!Number.isInteger(tier) || tier < 1 || tier > 6)) {
          errors.push(`计数型隐藏羁绊 ${synergy.id} 的来源品阶无效。`);
        }
      }
      if (
        repeatable.activationBoundary !== "after_prerequisite" ||
        repeatable.activationGrantCarriesForward !== false
      ) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 必须使用严格的激活后边界。`);
      }
      if (
        repeatable.afterSynergyId &&
        !synergies.some((candidate) => candidate.id === repeatable.afterSynergyId)
      ) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 引用了未知前置阶段。`);
      }
    }
    for (const suppressedId of synergy.suppressesSynergyIds ?? []) {
      if (
        suppressedId === synergy.id ||
        !synergies.some((candidate) => candidate.id === suppressedId)
      ) {
        errors.push(`计数型隐藏羁绊 ${synergy.id} 的压制关系无效。`);
      }
    }
    errors.push(...validateHiddenRuntimeEffects(synergy.effects, `隐性羁绊 ${synergy.id}`));
  }
  for (const effect of effects.filter((candidate) => candidate.startsLocked)) {
    if (!synergies.some((synergy) => synergy.activatesEffectIds.includes(effect.id))) {
      errors.push(`锁定的隐性效果 ${effect.id} 没有任何激活来源。`);
    }
  }
  for (const synergy of synergies) {
    const visited = new Set<string>();
    let current: HiddenSynergyDefinition | undefined = synergy;
    while (current?.repeatableRequirement?.afterSynergyId) {
      if (visited.has(current.id)) {
        errors.push(`计数型隐藏羁绊前置存在循环：${synergy.id}。`);
        break;
      }
      visited.add(current.id);
      const prerequisiteId: string =
        current.repeatableRequirement.afterSynergyId;
      current = synergies.find((candidate) => candidate.id === prerequisiteId);
    }
  }
  return errors;
}

function validateHiddenRuntimeEffects(
  effects: readonly HiddenRuntimeEffect[] | undefined,
  prefix: string,
): string[] {
  const errors: string[] = [];
  const kinds = new Set<HiddenRuntimeEffect["kind"]>([
    "periodic_area_damage",
    "periodic_random_damage",
    "ally_healing_aura",
    "periodic_heavy_damage",
    "ally_armor_aura",
    "ally_speed_aura",
    "periodic_normal_execute",
    "low_health_execute",
    "enemy_slow_immunity",
    "periodic_non_boss_push_damage",
    "base_move_speed_multiplier",
    "normal_enemy_taunt_aura",
    "every_nth_cast_double",
  ]);
  if (!Array.isArray(effects)) {
    return [`${prefix} 的效果必须是数组。`];
  }
  for (const effect of effects) {
    if (!kinds.has(effect.kind)) {
      errors.push(`${prefix} 包含未知效果 ${(effect as { kind?: unknown }).kind}。`);
      continue;
    }
    if (!hasOnlyFiniteNumbers(effect)) {
      errors.push(`${prefix} 的 ${effect.kind} 包含非法数值。`);
    }
    if ("radius" in effect && !isAllowedSkillRange(effect.radius)) {
      errors.push(`${prefix} 的 ${effect.kind} 范围不符合档位规则。`);
    }
    if (
      "intervalMs" in effect &&
      !isAllowedAttackIntervalMs(effect.intervalMs)
    ) {
      errors.push(`${prefix} 的 ${effect.kind} 间隔不符合档位规则。`);
    }
    if (
      effect.kind === "low_health_execute" &&
      (!positive(effect.healthRatio) || effect.healthRatio > 1)
    ) {
      errors.push(`${prefix} 的低生命处决阈值必须位于0到1之间。`);
    }
    if (
      (effect.kind === "periodic_area_damage" ||
        effect.kind === "periodic_random_damage" ||
        effect.kind === "periodic_heavy_damage" ||
        effect.kind === "periodic_non_boss_push_damage") &&
      !nonNegative(effect.damage)
    ) {
      errors.push(`${prefix} 的 ${effect.kind} 伤害不能为负数。`);
    }
    if (
      effect.kind === "ally_healing_aura" &&
      !nonNegative(effect.healingPerSecond)
    ) {
      errors.push(`${prefix} 的治疗数值不能为负数。`);
    }
    if (
      effect.kind === "ally_speed_aura" &&
      (!positive(effect.attackSpeedMultiplier) ||
        !positive(effect.moveSpeedMultiplier))
    ) {
      errors.push(`${prefix} 的速度倍率必须大于0。`);
    }
    if (
      effect.kind === "periodic_non_boss_push_damage" &&
      effect.pushOutsideRadius !== effect.radius
    ) {
      errors.push(`${prefix} periodic_non_boss_push_damage 的推出距离必须等于作用范围。`);
    }
    if (
      effect.kind === "base_move_speed_multiplier" &&
      !positive(effect.multiplier)
    ) {
      errors.push(`${prefix} base_move_speed_multiplier 的倍率必须大于0。`);
    }
    if (
      effect.kind === "normal_enemy_taunt_aura" &&
      effect.priority !== "standard"
    ) {
      errors.push(`${prefix} normal_enemy_taunt_aura 仅支持 standard 优先级。`);
    }
    if (
      effect.kind === "every_nth_cast_double" &&
      (!Number.isInteger(effect.every) || effect.every < 2)
    ) {
      errors.push(`${prefix} every_nth_cast_double 的触发次数必须是至少2的整数。`);
    }
  }
  return errors;
}

function validateSynergyContent(
  synergies: readonly SynergyDefinition[],
  modules: readonly ModuleDefinition[],
  hiddenEffects: readonly HiddenEffectDefinition[],
  sharedUnits: readonly UndeadUnitDefinition[],
): string[] {
  const errors: string[] = [];
  const moduleIds = new Set(modules.map((module) => module.id));
  const synergyIds = new Set<string>();
  const hiddenEffectIds = new Set(hiddenEffects.map((effect) => effect.id));
  const sharedUnitIds = new Set(sharedUnits.map((unit) => unit.id));
  const moduleById = new Map(modules.map((module) => [module.id, module]));
  const tags = new Set(modules.flatMap((module) => module.tags));
  const knownMechanics = new Set<SynergyMechanicDefinition["kind"]>([
    "stationary_stacks",
    "turret_transformation",
    "pasture_hunt",
    "hidden_effect_amplifier",
    "periodic_heavy_damage",
    "undead_catastrophe",
    "guard_critical",
    "guard_dash",
    "guard_oath_upgrade",
    "divine_guard_upgrade",
    "earth_spirit_aggregation",
    "follower_granted_skill",
    "day_night_cycle",
    "greed_paradise",
    "surface_mining_skill_amplifier",
    "material_guard_upgrade",
    "paper_units",
    "mining_exhaustion_bonus",
    "chaotic_mess",
    "debt_outsourcing",
    "night_shift_handoff",
    "overmaintenance",
    "needle_heal_counter",
    "dual_extremes_nth",
    "echo_transfer",
    "stacking_armor_break",
    "guard_coordination",
    "summon_death_cooldown",
    "follower_trio_combo",
    "stored_experience_companion_damage",
    "pain_enjoyment",
    "bitter_medicine",
    "paper_aircraft_carrier",
    "distant_target_bonus",
    "thunderstorm_counter",
    "freeze_combo",
    "soul_weighing",
    "hot_floor",
    "guard_council",
    "undead_guard",
    "nest_riot",
    "slacking_off",
    "spin_it",
    "suppress_modules",
    "max_health_regeneration",
    "long_range_strike",
    "thunder_god_indulgence",
    "absolute_domain",
    "fire_land",
    "blood_debt",
    "the_world",
    "greed_and_wealth",
    "dual_gods_agreement",
  ]);
  for (const synergy of synergies) {
    if (!ID_PATTERN.test(synergy.id) || !synergy.name?.trim() || !synergy.description?.trim()) {
      errors.push(`羁绊 ${synergy.id ?? "(无ID)"} 的ID、名称或说明无效。`);
    }
    if (synergyIds.has(synergy.id)) {
      errors.push(`羁绊ID重复：${synergy.id}。`);
    }
    synergyIds.add(synergy.id);
  }
  for (const synergy of synergies) {
    if (!Array.isArray(synergy.requirements) || synergy.requirements.length === 0) {
      errors.push(`羁绊 ${synergy.id} 至少需要一个前置条件。`);
    }
    if (!validateNumericRecord(synergy.bonuses)) {
      errors.push(`羁绊 ${synergy.id} 的属性加成包含非法数值。`);
    }
    for (const requirement of synergy.requirements ?? []) {
      if (requirement.kind === "module" && !moduleIds.has(requirement.moduleId)) {
        errors.push(`羁绊 ${synergy.id} 引用了未知技能 ${requirement.moduleId}。`);
      } else if (requirement.kind === "synergy" && !synergyIds.has(requirement.synergyId)) {
        errors.push(`羁绊 ${synergy.id} 引用了未知前置羁绊 ${requirement.synergyId}。`);
      } else if (requirement.kind === "one_of") {
        if (
          !Array.isArray(requirement.moduleIds) ||
          requirement.moduleIds.length === 0 ||
          requirement.moduleIds.some((id) => !moduleIds.has(id))
        ) {
          errors.push(`羁绊 ${synergy.id} 的任选技能前置包含未知或空引用。`);
        }
      } else if (requirement.kind === "tag_count") {
        if (!tags.has(requirement.tag) || !Number.isInteger(requirement.count) || requirement.count < 1) {
          errors.push(`羁绊 ${synergy.id} 的标签数量前置无效。`);
        }
      } else if (requirement.kind === "tier_count") {
        if (
          !Number.isInteger(requirement.minTier) ||
          requirement.minTier < 1 ||
          requirement.minTier > 6 ||
          !Number.isInteger(requirement.count) ||
          requirement.count < 1
        ) {
          errors.push(`羁绊 ${synergy.id} 的品阶数量前置无效。`);
        }
      } else if (!["module", "synergy", "one_of", "tag_count", "tier_count"].includes((requirement as { kind?: string }).kind ?? "")) {
        errors.push(`羁绊 ${synergy.id} 包含未知前置类型。`);
      }
    }
    for (const modifier of synergy.abilityModifiers ?? []) {
      errors.push(...validateAbilityModifier(modifier, synergy.id, moduleIds));
    }
    for (const mechanic of synergy.mechanics ?? []) {
      if (!knownMechanics.has(mechanic.kind)) {
        errors.push(`羁绊 ${synergy.id} 包含未知机制 ${(mechanic as { kind?: unknown }).kind}。`);
        continue;
      }
      if (!hasOnlyFiniteNumbers(mechanic)) {
        errors.push(`羁绊 ${synergy.id} 的 ${mechanic.kind} 包含非法数值。`);
      }
      for (const moduleId of getMechanicModuleReferences(mechanic)) {
        if (!moduleIds.has(moduleId)) {
          errors.push(`羁绊 ${synergy.id} 的 ${mechanic.kind} 引用了未知技能 ${moduleId}。`);
        }
      }
      if (
        mechanic.kind === "hidden_effect_amplifier" &&
        mechanic.hiddenEffectIds.some((id) => !hiddenEffectIds.has(id))
      ) {
        errors.push(`羁绊 ${synergy.id} 的隐性增幅引用了未知隐性效果。`);
      }
      if (
        mechanic.kind === "undead_catastrophe" &&
        [...mechanic.normalPool, ...mechanic.heavyPool].some(
          (entry) => !sharedUnitIds.has(entry.unitId) || !positive(entry.weight),
        )
      ) {
        errors.push(`羁绊 ${synergy.id} 的亡灵池引用或权重无效。`);
      }
      if (
        mechanic.kind === "undead_guard" &&
        !sharedUnitIds.has(mechanic.respawnSummonUnitId)
      ) {
        errors.push(`羁绊 ${synergy.id} 的护卫亡灵单位引用无效。`);
      }
      errors.push(
        ...validateSynergyMechanicRules(
          synergy.id,
          mechanic,
          moduleById,
          hiddenEffects,
          sharedUnitIds,
        ),
      );
    }
    for (const entry of getSynergySkillRangeEntries(synergy)) {
      if (!isAllowedSkillRange(entry.value)) {
        errors.push(
          `羁绊 ${synergy.id} 的${entry.label}不符合项目范围档位规则：${entry.value}。`,
        );
      }
    }
  }
  return errors;
}

function validateAbilityModifier(
  modifier: SynergyAbilityModifier,
  synergyId: string,
  moduleIds: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];
  if (
    !Array.isArray(modifier.moduleIds) ||
    modifier.moduleIds.length === 0 ||
    modifier.moduleIds.some((id) => !moduleIds.has(id))
  ) {
    errors.push(`羁绊 ${synergyId} 的技能修正引用了未知或空技能列表。`);
  }
  if (!hasOnlyFiniteNumbers(modifier)) {
    errors.push(`羁绊 ${synergyId} 的技能修正包含非法数值。`);
  }
  for (const key of [
    "damageMultiplier",
    "intervalMultiplier",
    "rangeMultiplier",
    "companionHealthMultiplier",
    "companionSpawnIntervalMultiplier",
    "cannonBaseDiameterMultiplier",
  ] as const) {
    const value = modifier[key];
    if (value !== undefined && value <= 0) {
      errors.push(`羁绊 ${synergyId} 的技能修正 ${key} 必须大于0。`);
    }
  }
  if (
    modifier.manaCostMultiplier !== undefined &&
    modifier.manaCostMultiplier < 0
  ) {
    errors.push(`羁绊 ${synergyId} 的技能修正能耗倍率不能为负数。`);
  }
  return errors;
}

function validateSynergyMechanicRules(
  synergyId: string,
  mechanic: SynergyMechanicDefinition,
  modules: ReadonlyMap<string, ModuleDefinition>,
  hiddenEffects: readonly HiddenEffectDefinition[],
  sharedUnitIds: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];
  const invalid = (label: string): void => {
    errors.push(`羁绊 ${synergyId} 的${label}配置无效。`);
  };
  if (
    mechanic.kind === "turret_transformation" &&
    !isAllowedSkillRange(mechanic.tauntRadius)
  ) {
    invalid("炮台形态");
  } else if (
    mechanic.kind === "pasture_hunt" &&
    (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
      !isAllowedSkillRange(mechanic.range) ||
      !positive(mechanic.damagePerTotalTier))
  ) {
    invalid("牧灵游猎");
  } else if (mechanic.kind === "hidden_effect_amplifier") {
    const referenced = hiddenEffects.filter((effect) =>
      mechanic.hiddenEffectIds.includes(effect.id),
    );
    if (
      mechanic.effectMultiplier < 1 ||
      mechanic.hiddenEffectIds.length === 0 ||
      referenced.length !== mechanic.hiddenEffectIds.length ||
      referenced.some((effect) =>
        effect.effects.some(
          (entry) =>
            entry.kind === "periodic_normal_execute" &&
            !isAllowedAttackIntervalMs(
              entry.intervalMs / mechanic.effectMultiplier,
            ),
        ),
      )
    ) {
      invalid("隐性效果增幅");
    }
  } else if (
    mechanic.kind === "periodic_heavy_damage" &&
    (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
      !isAllowedSkillRange(mechanic.range) ||
      !positive(mechanic.damage))
  ) {
    invalid("重型单位周期伤害");
  } else if (mechanic.kind === "undead_catastrophe") {
    const validPool = (
      pool: typeof mechanic.normalPool,
    ): boolean =>
      pool.length > 0 &&
      pool.reduce((sum, entry) => sum + entry.weight, 0) === 100 &&
      pool.every(
        (entry) =>
          positive(entry.weight) && sharedUnitIds.has(entry.unitId),
      );
    if (
      mechanic.summonChance < 0 ||
      mechanic.summonChance > 1 ||
      !modules.has(mechanic.triggerAbilityId) ||
      !validPool(mechanic.normalPool) ||
      !validPool(mechanic.heavyPool)
    ) {
      invalid("亡灵天灾");
    }
  } else if (
    mechanic.kind === "guard_critical" &&
    (!modules.has(mechanic.targetAbilityId) ||
      mechanic.damageAdd < 0 ||
      !Number.isInteger(mechanic.attacksPerCritical) ||
      mechanic.attacksPerCritical < 2 ||
      mechanic.criticalDamageMultiplier <= 1)
  ) {
    invalid("护卫暴击");
  } else if (
    mechanic.kind === "guard_dash" &&
    (!modules.has(mechanic.targetAbilityId) ||
      mechanic.attackSpeedBonus < 0 ||
      mechanic.moveSpeedBonus < 0 ||
      !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
      !isAllowedSkillRange(mechanic.acquireRange) ||
      !isAllowedSkillRange(mechanic.pathWidth) ||
      !positive(mechanic.damage))
  ) {
    invalid("护卫冲刺");
  } else if (
    mechanic.kind === "guard_oath_upgrade" &&
    (!modules.has(mechanic.targetAbilityId) ||
      !modules.has(mechanic.consumedAbilityId) ||
      !positive(mechanic.health) ||
      !positive(mechanic.damage) ||
      mechanic.armor < 0 ||
      !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
      !isAllowedSkillRange(mechanic.range) ||
      !positive(mechanic.moveSpeedBaseRatio) ||
      !positive(mechanic.respawnMs))
  ) {
    invalid("誓约护卫升格");
  } else if (
    mechanic.kind === "divine_guard_upgrade" &&
    (!modules.has(mechanic.targetAbilityId) ||
      mechanic.suppressedModuleIds.length === 0 ||
      mechanic.suppressedModuleIds.some((id) => !modules.has(id)) ||
      mechanic.positiveStatMultiplier < 1 ||
      mechanic.healthAdd < 0 ||
      mechanic.damageAdd < 0 ||
      mechanic.armorAdd < 0 ||
      mechanic.attackSpeedBonus < 0 ||
      mechanic.moveSpeedBonus < 0 ||
      mechanic.abilityDamageBonus < 0 ||
      !isAllowedSkillRange(mechanic.range) ||
      !positive(mechanic.respawnMs) ||
      !isAllowedAttackIntervalMs(mechanic.holyFireIntervalMs) ||
      !isAllowedSkillRange(mechanic.holyFireRadius) ||
      !positive(mechanic.holyFireDamage) ||
      !Number.isInteger(mechanic.judgmentAttacksPerCritical) ||
      mechanic.judgmentAttacksPerCritical < 2 ||
      mechanic.judgmentDamageMultiplier <= 1 ||
      !isAllowedAttackIntervalMs(mechanic.descentIntervalMs) ||
      !isAllowedSkillRange(mechanic.descentAcquireRange) ||
      !isAllowedSkillRange(mechanic.descentRadius) ||
      !positive(mechanic.descentDamage))
  ) {
    invalid("神圣护卫升格");
  } else if (
    mechanic.kind === "earth_spirit_aggregation" &&
    (!modules.has(mechanic.targetAbilityId) ||
      !isAllowedSkillRange(mechanic.aggregationRange) ||
      mechanic.participantsRequired !== 3 ||
      mechanic.maxStar !== 5 ||
      !positive(mechanic.baseHealth) ||
      mechanic.baseArmor < 0 ||
      !positive(mechanic.baseDamage) ||
      !isAllowedAttackIntervalMs(mechanic.baseIntervalMs) ||
      !isAllowedSkillRange(mechanic.baseRange) ||
      !positive(mechanic.baseMoveSpeedRatio) ||
      mechanic.healthMultiplierPerStar < 1 ||
      mechanic.armorMultiplierPerStar < 1 ||
      mechanic.damageMultiplierPerStar < 1 ||
      !positive(mechanic.healthRoundUpTo) ||
      !positive(mechanic.armorRoundUpTo) ||
      !positive(mechanic.damageRoundUpTo) ||
      !isAllowedSkillRange(mechanic.rulerAuraRadius) ||
      mechanic.rulerAttackSpeedBonus < 0)
  ) {
    invalid("土灵聚合");
  } else if (mechanic.kind === "follower_granted_skill") {
    const target = modules.get(mechanic.targetAbilityId);
    const granted = modules.get(mechanic.grantedAbilityId);
    if (target?.category !== "follower" || !granted?.combat) {
      invalid("随从授予技能");
    }
  } else if (
    mechanic.kind === "day_night_cycle" &&
    (!positive(mechanic.dayDurationMs) ||
      !positive(mechanic.nightDurationMs) ||
      !isAllowedSkillRange(mechanic.range) ||
      !isAllowedAttackIntervalMs(mechanic.dayIntervalMs) ||
      !isAllowedAttackIntervalMs(mechanic.nightIntervalMs) ||
      !positive(mechanic.dayDamage) ||
      !positive(mechanic.nightDamage))
  ) {
    invalid("日夜轮转");
  } else if (mechanic.kind === "greed_paradise") {
    const forced = modules.get(mechanic.forcedAggroAbilityId);
    const growth = modules.get(mechanic.growthAbilityId);
    if (
      !forced?.mechanics?.some(
        (entry) => entry.kind === "periodic_forced_aggro",
      ) ||
      !growth?.mechanics?.some(
        (entry) => entry.kind === "ability_kill_growth",
      ) ||
      !isAllowedAttackIntervalMs(mechanic.forcedDamageIntervalMs) ||
      !positive(mechanic.forcedDamage) ||
      !Number.isInteger(mechanic.killsPerDamageGrowth) ||
      mechanic.killsPerDamageGrowth < 1 ||
      !positive(mechanic.damagePerSettlement)
    ) {
      invalid("贪欲乐园");
    }
  } else if (mechanic.kind === "surface_mining_skill_amplifier") {
    if (
      !isFiniteNumber(mechanic.positiveEffectMultiplier) ||
      mechanic.positiveEffectMultiplier < 1 ||
      !Array.isArray(mechanic.excludedModuleIds) ||
      mechanic.excludedModuleIds.some((moduleId) => {
        const module = modules.get(moduleId);
        return !module || module.category !== "mining";
      })
    ) {
      invalid("轻量地表采矿正向效果增幅");
    }
  }
  return errors;
}

function getMechanicModuleReferences(
  mechanic: SynergyMechanicDefinition,
): string[] {
  switch (mechanic.kind) {
    case "undead_catastrophe":
      return [mechanic.triggerAbilityId];
    case "guard_critical":
    case "guard_dash":
      return [mechanic.targetAbilityId];
    case "guard_oath_upgrade":
      return [mechanic.targetAbilityId, mechanic.consumedAbilityId];
    case "divine_guard_upgrade":
      return [mechanic.targetAbilityId, ...mechanic.suppressedModuleIds];
    case "earth_spirit_aggregation":
      return [mechanic.targetAbilityId];
    case "follower_granted_skill":
      return [mechanic.targetAbilityId, mechanic.grantedAbilityId];
    case "greed_paradise":
      return [mechanic.forcedAggroAbilityId, mechanic.growthAbilityId];
    case "surface_mining_skill_amplifier":
      return [...mechanic.excludedModuleIds];
    case "material_guard_upgrade":
      return [mechanic.targetAbilityId, mechanic.consumedAbilityId];
    case "paper_units":
    case "distant_target_bonus":
    case "thunderstorm_counter":
    case "freeze_combo":
    case "hot_floor":
    case "guard_council":
    case "nest_riot":
    case "dual_extremes_nth":
    case "echo_transfer":
    case "stacking_armor_break":
    case "guard_coordination":
    case "summon_death_cooldown":
    case "follower_trio_combo":
    case "stored_experience_companion_damage":
      return [...mechanic.abilityIds];
    case "needle_heal_counter":
      return [...mechanic.abilityIds, mechanic.healAbilityId];
    case "bitter_medicine":
      return [...mechanic.healingAbilityIds];
    case "chaotic_mess":
      return [
        mechanic.brazierAbilityId,
        mechanic.acidAbilityId,
        mechanic.mixerAbilityId,
      ];
    case "debt_outsourcing":
      return [mechanic.crowAbilityId];
    case "pain_enjoyment":
      return [mechanic.growthAbilityId];
    case "paper_aircraft_carrier":
      return [
        mechanic.hiveAbilityId,
        mechanic.kiteAbilityId,
        mechanic.whaleAbilityId,
      ];
    case "soul_weighing":
      return [
        mechanic.echoAbilityId,
        mechanic.boomerangAbilityId,
        mechanic.balanceAbilityId,
      ];
    case "undead_guard":
      return [mechanic.targetAbilityId];
    case "spin_it":
      return [mechanic.gearAbilityId];
    case "suppress_modules":
      return [...mechanic.moduleIds];
    case "long_range_strike":
    case "thunder_god_indulgence":
      return [...mechanic.abilityIds];
    case "absolute_domain":
    case "fire_land":
    case "the_world":
      return [...mechanic.sourceAbilityIds];
    case "blood_debt":
      return [
        mechanic.healthCostAbilityId,
        mechanic.empoweredAbilityId,
        mechanic.lifestealAbilityId,
      ];
    case "greed_and_wealth":
      return [...mechanic.suppressedModuleIds];
    case "dual_gods_agreement":
      return [
        ...mechanic.contractAbilityIds,
        ...mechanic.randomCastAbilityIds,
      ];
    default:
      return [];
  }
}

function getSynergyCycleIds(
  synergies: readonly SynergyDefinition[],
): Set<string> {
  const byId = new Map(synergies.map((synergy) => [synergy.id, synergy]));
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const cycles = new Set<string>();
  const visit = (id: string): void => {
    if (visiting.has(id)) {
      cycles.add(id);
      return;
    }
    if (visited.has(id)) {
      return;
    }
    visiting.add(id);
    for (const requirement of byId.get(id)?.requirements ?? []) {
      if (requirement.kind === "synergy") {
        visit(requirement.synergyId);
        if (cycles.has(requirement.synergyId)) {
          cycles.add(id);
        }
      }
    }
    visiting.delete(id);
    visited.add(id);
  };
  for (const synergy of synergies) {
    visit(synergy.id);
  }
  return cycles;
}

function resolveSynergySlotOptions(
  id: string,
  synergies: ReadonlyMap<string, SynergyDefinition>,
  modules: ReadonlyMap<string, ModuleDefinition>,
  cache: Map<string, ReadonlySet<string>[]>,
  visiting: Set<string>,
): ReadonlySet<string>[] {
  const cached = cache.get(id);
  if (cached) {
    return cached;
  }
  if (visiting.has(id)) {
    return [];
  }
  const synergy = synergies.get(id);
  if (!synergy) {
    return [];
  }
  visiting.add(id);
  let options: ReadonlySet<string>[] = [new Set()];
  for (const requirement of synergy.requirements) {
    let requirementOptions: ReadonlySet<string>[] = [];
    if (requirement.kind === "module") {
      requirementOptions = modules.has(requirement.moduleId)
        ? [new Set([requirement.moduleId])]
        : [];
    } else if (requirement.kind === "one_of") {
      requirementOptions = requirement.moduleIds
        .filter((moduleId) => modules.has(moduleId))
        .map((moduleId) => new Set([moduleId]));
    } else if (requirement.kind === "tag_count") {
      requirementOptions = buildCombinationSets(
        [...modules.values()]
          .filter((module) => module.tags.includes(requirement.tag))
          .map((module) => module.id),
        requirement.count,
      );
    } else if (requirement.kind === "tier_count") {
      requirementOptions = buildCombinationSets(
        [...modules.values()]
          .filter((module) => module.tier >= requirement.minTier)
          .map((module) => module.id),
        requirement.count,
      );
    } else {
      requirementOptions = resolveSynergySlotOptions(
        requirement.synergyId,
        synergies,
        modules,
        cache,
        visiting,
      );
    }
    options = combineSlotOptions(options, requirementOptions);
    if (options.length === 0) {
      break;
    }
  }
  visiting.delete(id);
  const pruned = pruneSlotOptions(options);
  cache.set(id, pruned);
  return pruned;
}

function buildCombinationSets(
  ids: readonly string[],
  count: number,
): ReadonlySet<string>[] {
  if (!Number.isInteger(count) || count < 1 || ids.length < count) {
    return [];
  }
  const result: ReadonlySet<string>[] = [];
  const selected: string[] = [];
  const visit = (start: number): void => {
    if (result.length >= 2_000) {
      return;
    }
    if (selected.length === count) {
      result.push(new Set(selected));
      return;
    }
    for (
      let index = start;
      index <= ids.length - (count - selected.length);
      index += 1
    ) {
      selected.push(ids[index]!);
      visit(index + 1);
      selected.pop();
    }
  };
  visit(0);
  return result;
}

function combineSlotOptions(
  left: readonly ReadonlySet<string>[],
  right: readonly ReadonlySet<string>[],
): ReadonlySet<string>[] {
  if (left.length === 0 || right.length === 0) {
    return [];
  }
  const combined: ReadonlySet<string>[] = [];
  for (const first of left) {
    for (const second of right) {
      combined.push(new Set([...first, ...second]));
      if (combined.length >= 10_000) {
        return pruneSlotOptions(combined);
      }
    }
  }
  return pruneSlotOptions(combined);
}

function pruneSlotOptions(
  options: readonly ReadonlySet<string>[],
): ReadonlySet<string>[] {
  const unique = new Map<string, ReadonlySet<string>>();
  for (const option of options) {
    const key = [...option].sort().join("|");
    unique.set(key, option);
  }
  const sorted = [...unique.values()].sort((a, b) => a.size - b.size);
  const kept: ReadonlySet<string>[] = [];
  for (const option of sorted) {
    if (
      kept.some(
        (candidate) =>
          candidate.size <= option.size &&
          [...candidate].every((id) => option.has(id)),
      )
    ) {
      continue;
    }
    kept.push(option);
    if (kept.length >= 500) {
      break;
    }
  }
  return kept;
}

function buildWorkshopPreview(
  content: WorkshopActiveContent,
  slotOptionsById: ReadonlyMap<string, readonly ReadonlySet<string>[]>,
): WorkshopPreview {
  const tierCounts: Record<string, number> = {};
  const categoryCounts: Record<string, number> = {};
  for (const module of content.modules) {
    tierCounts[String(module.tier)] = (tierCounts[String(module.tier)] ?? 0) + 1;
    categoryCounts[module.category] =
      (categoryCounts[module.category] ?? 0) + 1;
  }
  const directSynergiesForSkill = (skill: ModuleDefinition): string[] =>
    content.synergies.flatMap((synergy) =>
      synergy.requirements.some((requirement) =>
        requirement.kind === "module"
          ? requirement.moduleId === skill.id
          : requirement.kind === "one_of"
            ? requirement.moduleIds.includes(skill.id)
            : requirement.kind === "tag_count"
              ? skill.tags.includes(requirement.tag)
              : requirement.kind === "tier_count"
                ? skill.tier >= requirement.minTier
                : false,
      )
        ? [synergy.id]
        : [],
    );
  const skillMetrics = Object.fromEntries(
    content.modules.map((module) => {
      const counterpartId = module.evolutionTo ?? module.evolutionFrom;
      const directConditionalIds = getReferencedSkillTargetIds(module);
      const incomingConditionalIds = content.modules.flatMap((candidate) =>
        getReferencedSkillTargetIds(candidate).includes(module.id)
          ? [candidate.id]
          : [],
      );
      const conditionalEvolutionIds = [
        ...new Set([...directConditionalIds, ...incomingConditionalIds]),
      ];
      const conditionalCounterpartId = conditionalEvolutionIds[0] ?? null;
      const counterpart = counterpartId
        ? content.modules.find((candidate) => candidate.id === counterpartId)
        : undefined;
      const base =
        module.tier === 6 ? counterpart : module;
      const ascended =
        module.tier === 6 ? module : counterpart;
      const baseDps = base
        ? getModulePaperDps(base, content.sharedUnits)
        : 0;
      const ascendedDps = ascended
        ? getModulePaperDps(ascended, content.sharedUnits)
        : 0;
      const metric: SkillPreview = {
        id: module.id,
        paperDps: getModulePaperDps(module, content.sharedUnits),
        ascensionRatio:
          baseDps > 0 && ascendedDps > 0 ? ascendedDps / baseDps : null,
        evolutionId: counterpartId ?? null,
        conditionalEvolutionId: conditionalCounterpartId,
        conditionalEvolutionIds,
        conditionalEvolutionCount:
          module.experienceVault?.capacity ??
          module.killCharge?.requiredCount ??
          (module.conditionalEvolution?.kind ===
          "surface_mining_devour_count"
            ? module.conditionalEvolution.requiredCount
            : module.conditionalEvolution?.kind === "player_level"
              ? module.conditionalEvolution.requiredLevel
              : null),
        synergyIds: directSynergiesForSkill(module),
        dependentSynergyIds: content.synergies.flatMap((synergy) =>
          (synergy.abilityModifiers ?? []).some((modifier) =>
            modifier.moduleIds.includes(module.id),
          ) ||
          (synergy.mechanics ?? []).some((mechanic) =>
            getMechanicModuleReferences(mechanic).includes(module.id),
          )
            ? [synergy.id]
            : [],
        ),
      };
      return [module.id, metric];
    }),
  );
  const synergyMetrics = Object.fromEntries(
    content.synergies.map((synergy) => {
      const options = slotOptionsById.get(synergy.id) ?? [];
      const uniqueSlots =
        options.length > 0
          ? Math.min(...options.map((option) => option.size))
          : null;
      const metric: SynergyPreview = {
        id: synergy.id,
        uniqueSlots,
        reachable: uniqueSlots !== null && uniqueSlots <= 6,
        dependencyIds: synergy.requirements.flatMap((requirement) =>
          requirement.kind === "synergy" ? [requirement.synergyId] : [],
        ),
        dependentIds: content.synergies.flatMap((candidate) =>
          candidate.requirements.some(
            (requirement) =>
              requirement.kind === "synergy" &&
              requirement.synergyId === synergy.id,
          )
            ? [candidate.id]
            : [],
        ),
      };
      return [synergy.id, metric];
    }),
  );
  return {
    tierCounts,
    categoryCounts,
    skillMetrics,
    synergyMetrics,
  };
}

function collectWorkshopDependencies(
  content: WorkshopActiveContent,
): WorkshopValidation["dependencies"] {
  const empty = (): WorkshopDependency => ({
    evolutionIds: [],
    synergyIds: [],
    grantIds: [],
    referencedByIds: [],
  });
  const dependencies: WorkshopValidation["dependencies"] = {
    skills: Object.fromEntries(content.modules.map((entry) => [entry.id, empty()])),
    synergies: Object.fromEntries(content.synergies.map((entry) => [entry.id, empty()])),
    hiddenEffects: Object.fromEntries(content.hiddenEffects.map((entry) => [entry.id, empty()])),
    hiddenSynergies: Object.fromEntries(content.hiddenSynergies.map((entry) => [entry.id, empty()])),
    sharedUnits: Object.fromEntries(content.sharedUnits.map((entry) => [entry.id, empty()])),
  };
  for (const module of content.modules) {
    dependencies.skills[module.id]!.evolutionIds = [
      module.evolutionFrom,
      module.evolutionTo,
      ...getReferencedSkillTargetIds(module),
    ].filter((id): id is string => Boolean(id));
    for (const targetId of getReferencedSkillTargetIds(module)) {
      dependencies.skills[targetId]?.referencedByIds.push(module.id);
    }
    dependencies.skills[module.id]!.grantIds = [
      ...(module.grantsHiddenEffectIds ?? []),
      ...(module.onAcquire?.repeatableHiddenEffectId
        ? [module.onAcquire.repeatableHiddenEffectId]
        : []),
      ...(module.labor && "hiddenEffectId" in module.labor
        ? [module.labor.hiddenEffectId]
        : []),
    ];
    for (const effectId of dependencies.skills[module.id]!.grantIds) {
      dependencies.hiddenEffects[effectId]?.referencedByIds.push(module.id);
    }
    for (const mechanic of module.mechanics ?? []) {
      if (mechanic.kind === "undead_summon") {
        dependencies.sharedUnits[mechanic.unitId]?.referencedByIds.push(module.id);
      }
    }
  }
  for (const synergy of content.synergies) {
    for (const requirement of synergy.requirements) {
      if (requirement.kind === "module") {
        dependencies.skills[requirement.moduleId]?.synergyIds.push(synergy.id);
      } else if (requirement.kind === "one_of") {
        for (const moduleId of requirement.moduleIds) {
          dependencies.skills[moduleId]?.synergyIds.push(synergy.id);
        }
      } else if (requirement.kind === "tag_count") {
        for (const module of content.modules.filter((candidate) =>
          candidate.tags.includes(requirement.tag),
        )) {
          dependencies.skills[module.id]?.synergyIds.push(synergy.id);
        }
      } else if (requirement.kind === "tier_count") {
        for (const module of content.modules.filter(
          (candidate) => candidate.tier >= requirement.minTier,
        )) {
          dependencies.skills[module.id]?.synergyIds.push(synergy.id);
        }
      } else if (requirement.kind === "synergy") {
        dependencies.synergies[requirement.synergyId]?.referencedByIds.push(
          synergy.id,
        );
      }
    }
    for (const modifier of synergy.abilityModifiers ?? []) {
      for (const moduleId of modifier.moduleIds) {
        dependencies.skills[moduleId]?.synergyIds.push(synergy.id);
      }
    }
    for (const mechanic of synergy.mechanics ?? []) {
      for (const moduleId of getMechanicModuleReferences(mechanic)) {
        dependencies.skills[moduleId]?.synergyIds.push(synergy.id);
      }
      if (mechanic.kind === "hidden_effect_amplifier") {
        for (const effectId of mechanic.hiddenEffectIds) {
          dependencies.hiddenEffects[effectId]?.referencedByIds.push(synergy.id);
        }
      }
      if (mechanic.kind === "undead_catastrophe") {
        for (const entry of [...mechanic.normalPool, ...mechanic.heavyPool]) {
          dependencies.sharedUnits[entry.unitId]?.referencedByIds.push(synergy.id);
        }
      }
    }
  }
  for (const hiddenSynergy of content.hiddenSynergies) {
    for (const effectId of [
      ...hiddenSynergy.requiredEffectIds,
      ...hiddenSynergy.activatesEffectIds,
    ]) {
      dependencies.hiddenEffects[effectId]?.referencedByIds.push(
        hiddenSynergy.id,
      );
    }
    const requirement = hiddenSynergy.repeatableRequirement;
    if (requirement) {
      dependencies.hiddenEffects[
        requirement.effectId
      ]?.referencedByIds.push(hiddenSynergy.id);
      if (requirement.afterSynergyId) {
        dependencies.hiddenSynergies[
          requirement.afterSynergyId
        ]?.referencedByIds.push(hiddenSynergy.id);
      }
    }
    for (const suppressedId of hiddenSynergy.suppressesSynergyIds ?? []) {
      dependencies.hiddenSynergies[suppressedId]?.referencedByIds.push(
        hiddenSynergy.id,
      );
    }
  }
  for (const group of Object.values(dependencies)) {
    for (const dependency of Object.values(group)) {
      dependency.evolutionIds = [...new Set(dependency.evolutionIds)];
      dependency.synergyIds = [...new Set(dependency.synergyIds)];
      dependency.grantIds = [...new Set(dependency.grantIds)];
      dependency.referencedByIds = [...new Set(dependency.referencedByIds)];
    }
  }
  return dependencies;
}

function hasOnlyFiniteNumbers(value: unknown): boolean {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every(hasOnlyFiniteNumbers);
  }
  const object = asRecord(value);
  return object
    ? Object.values(object).every(hasOnlyFiniteNumbers)
    : true;
}

function validateEditableModule(
  module: ModuleDefinition,
  sharedUnits?: readonly UndeadUnitDefinition[],
): string[] {
  const errors: string[] = [];
  if (!asRecord(module)) {
    return ["技能必须是对象。"];
  }
  if (typeof module.id !== "string" || !ID_PATTERN.test(module.id)) {
    errors.push("ID必须以小写字母开头，只能包含小写字母、数字和下划线。");
  }
  if (typeof module.name !== "string" || module.name.trim().length === 0) {
    errors.push("技能名称不能为空。");
  }
  if (
    typeof module.description !== "string" ||
    module.description.trim().length === 0
  ) {
    errors.push("技能说明不能为空。");
  }
  if (!Number.isInteger(module.tier) || module.tier < 1 || module.tier > 6) {
    errors.push("技能品阶必须是1到6的整数。");
  }
  if (!ABILITY_CATEGORIES.includes(module.category)) {
    errors.push("技能分类无效。");
  }
  if (module.category === "mining") {
    const devourSpecial = Boolean(
      module.tier === 3 &&
        module.hidden &&
        module.weight === 0 &&
        module.surfaceMining?.devour &&
        module.conditionalEvolution,
    );
    const contactSpecial = Boolean(
      module.tier === 6 &&
        module.hidden &&
        module.weight === 0 &&
        module.surfaceMining?.contactAttack,
    );
    if (module.tier > 2 && !devourSpecial && !contactSpecial) {
      errors.push("轻量地表采矿技能只能处于一阶或二阶。");
    }
    if (
      !module.surfaceMining ||
      ![1, 2].includes(module.surfaceMining.schemaVersion)
    ) {
      errors.push("轻量地表采矿技能必须配置 surfaceMining v1/v2。");
    }
    if (module.evolutionTo || module.evolutionFrom) {
      errors.push("轻量地表采矿技能不得配置五升六关系。");
    }
  } else if (module.surfaceMining) {
    errors.push("非采矿技能不得配置 surfaceMining。");
  }
  if (module.surfaceMining) {
    const mining = module.surfaceMining;
    if (
      mining.settlementIntervalMultiplier !== undefined &&
      (!positive(mining.settlementIntervalMultiplier) ||
        mining.settlementIntervalMultiplier > 1)
    ) {
      errors.push("采集结算间隔倍率必须大于0且不高于1。");
    }
    for (const [label, value] of [
      ["额外采集量", mining.extraGoldFlat],
      ["额外采集倍率", mining.extraGoldMultiplier],
      ["加法采矿速度", mining.speedBonus],
      ["最终金币加成", mining.goldYieldBonus],
      ["条件反隐倍率", mining.conditionalRevealRadiusBonus],
      ["结晶比例", mining.crystalYieldRatio],
      ["反隐范围增量", mining.revealRadiusAdd],
      ["侦查范围增量", mining.scoutingRadiusAdd],
    ] as const) {
      if (value !== undefined && !nonNegative(value)) {
        errors.push(`${label}必须是非负有限数。`);
      }
    }
    if (
      mining.advancedDiscoveryIds !== undefined &&
      (!Array.isArray(mining.advancedDiscoveryIds) ||
        mining.advancedDiscoveryIds.some(
          (id) => typeof id !== "string" || !ID_PATTERN.test(id),
        ))
    ) {
      errors.push("高级发现机制ID必须是有效ID数组。");
    }
    if (
      mining.manaPoweredSpeed &&
      (!positive(mining.manaPoweredSpeed.speedBonus) ||
        !positive(mining.manaPoweredSpeed.manaCostPerSettlement))
    ) {
      errors.push("魔力驱动采矿速度和每次结算耗蓝必须大于0。");
    }
    if (
      mining.devour &&
      (module.tier !== 3 ||
        !module.hidden ||
        module.weight !== 0 ||
        !positive(mining.devour.channelMs) ||
        !positive(mining.devour.cooldownMs) ||
        !positive(mining.devour.goldPerRemaining) ||
        !positive(mining.devour.moveSpeedPenaltyPerSuccess) ||
        mining.devour.moveSpeedPenaltyPerSuccess >= 1)
    ) {
      errors.push("隐藏吞噬配置无效。");
    }
    if (
      mining.contactAttack &&
      (module.tier !== 6 ||
        !module.hidden ||
        module.weight !== 0 ||
        !positive(mining.contactAttack.fixedBaseMoveSpeedRatio) ||
        mining.contactAttack.fixedBaseMoveSpeedRatio >= 1 ||
        !positive(mining.contactAttack.damage) ||
        !positive(mining.contactAttack.enemyCooldownMs) ||
        !nonNegative(mining.contactAttack.knockbackDistance) ||
        !nonNegative(mining.contactAttack.healOnKill))
    ) {
      errors.push("隐藏接触攻击配置无效。");
    }
  }
  if (module.conditionalEvolution) {
    const evolution = module.conditionalEvolution;
    const valid =
      evolution.kind === "surface_mining_devour_count"
        ? module.tier === 3 &&
          Boolean(module.surfaceMining?.devour) &&
          Number.isInteger(evolution.requiredCount) &&
          evolution.requiredCount >= 1 &&
          ID_PATTERN.test(evolution.targetModuleId) &&
          evolution.sameSlot === true
        : Number.isInteger(evolution.requiredLevel) &&
          evolution.requiredLevel >= 1 &&
          evolution.requiredLevel <= 20 &&
          ID_PATTERN.test(evolution.targetModuleId) &&
          evolution.sameSlot === true;
    if (!valid) {
      errors.push("条件进阶配置无效。");
    }
  }
  if (module.experienceVault) {
    const vault = module.experienceVault;
    const evolution = vault.evolution;
    const evolutionValid =
      evolution?.kind === "fixed"
        ? ID_PATTERN.test(evolution.targetModuleId)
        : evolution?.kind === "weighted" &&
          Array.isArray(evolution.branches) &&
          evolution.branches.length >= 2 &&
          evolution.branches.every(
            (branch) =>
              ID_PATTERN.test(branch.targetModuleId) &&
              positive(branch.weight),
          ) &&
          new Set(evolution.branches.map((branch) => branch.targetModuleId))
            .size === evolution.branches.length &&
          Math.abs(
            evolution.branches.reduce(
              (sum, branch) => sum + branch.weight,
              0,
            ) - 100,
          ) < 1e-9;
    if (
      !positive(vault.capacity) ||
      !nonNegative(vault.replacementPayoutMultiplier) ||
      vault.suppressPayoutOnEvolution !== true ||
      !evolutionValid
    ) {
      errors.push("经验仓、替换返还或条件概率进阶配置无效。");
    }
  }
  if (
    module.killCharge &&
    (module.killCharge.trigger !== "direct_kill" ||
      !Number.isInteger(module.killCharge.requiredCount) ||
      module.killCharge.requiredCount < 1 ||
      !ID_PATTERN.test(module.killCharge.targetModuleId) ||
      module.killCharge.sameSlot !== true)
  ) {
    errors.push("逐槽直接击杀充能配置无效。");
  }
  if (
    !Array.isArray(module.tags) ||
    module.tags.some((tag) => typeof tag !== "string" || tag.trim() === "")
  ) {
    errors.push("标签必须是非空字符串数组。");
  }
  if (!isFiniteNumber(module.weight) || module.weight < 0) {
    errors.push("随机权重必须是大于等于0的有限数字。");
  }
  if (!validateNumericRecord(module.bonuses)) {
    errors.push("属性强化必须是只包含有限数字的对象。");
  }
  if (
    module.hidden !== undefined &&
    typeof module.hidden !== "boolean"
  ) {
    errors.push("隐藏技能标记必须是布尔值。");
  }
  if (module.combat) {
    if (
      !nonNegative(module.combat.damage) ||
      !nonNegative(module.combat.range) ||
      !TARGET_MODES.includes(module.combat.target)
    ) {
      errors.push("攻击效果的伤害、范围或目标模式无效。");
    }
    if (!isAllowedAttackIntervalMs(module.combat.intervalMs)) {
      errors.push(
        "攻击间隔必须使用项目允许的秒数档位（如0.05、0.15、0.8、1.5、2.5或整数秒）。",
      );
    }
    validateOptionalNumbers(module.combat, errors, "攻击效果");
  }
  if (module.companion) {
    if (
      !COMPANION_KINDS.includes(module.companion.kind) ||
      typeof module.companion.presentationId !== "string" ||
      !Number.isInteger(module.companion.count) ||
      module.companion.count < 1 ||
      !nonNegative(module.companion.orbitRadius) ||
      !nonNegative(module.companion.moveSpeed)
    ) {
      errors.push("伙伴效果的类型、模型接口、数量或移动参数无效。");
    }
    if (
      module.companion.intervalMs !== undefined &&
      !isAllowedAttackIntervalMs(module.companion.intervalMs)
    ) {
      errors.push(
        "伙伴攻击间隔必须使用项目允许的秒数档位（如0.05、0.15、0.8、1.5、2.5或整数秒）。",
      );
    }
    const movement = module.companion.movement;
    if (
      movement &&
      (movement.kind === "player_centered_wander"
        ? !isAllowedSkillRange(movement.wanderRadius) ||
          !isAllowedSkillRange(movement.returnTriggerRadius) ||
          !isAllowedSkillRange(movement.returnReleaseRadius) ||
          movement.returnReleaseRadius >= movement.returnTriggerRadius ||
          !nonNegative(movement.wanderSpeedRatio) ||
          !nonNegative(movement.returnSpeedRatio)
        : !isAllowedSkillRange(movement.radius) ||
          !positive(movement.speedBaseRatio))
    ) {
      errors.push("伙伴移动必须使用有效的范围档位和速度比例。");
    }
    validateOptionalNumbers(module.companion, errors, "伙伴效果");
  }
  if (module.economy) {
    if (
      ![
        "periodic",
        "move_distance",
        "attack",
        "repair",
      ].includes(module.economy.trigger)
    ) {
      errors.push("经济效果触发方式无效。");
    }
    validateOptionalNumbers(module.economy, errors, "经济效果");
  }
  if (module.growth) {
    if (
      !positive(module.growth.every) ||
      (module.growth.amount !== undefined && !isFiniteNumber(module.growth.amount)) ||
      (module.growth.amount === undefined && !module.growth.bonuses) ||
      (module.growth.everyReductionPerSettlement !== undefined &&
        (!positive(module.growth.everyReductionPerSettlement) ||
          !positive(module.growth.minEvery ?? 0) ||
          (module.growth.minEvery ?? 0) > module.growth.every))
    ) {
      errors.push("成长效果的达成次数和成长数值无效。");
    }
    validateOptionalNumbers(module.growth, errors, "成长效果");
  }
  if (module.onAcquire) {
    validateOptionalNumbers(module.onAcquire, errors, "单次效果");
    if (module.category !== "one_shot") {
      errors.push("获得时单次效果只能用于单次技能。");
    }
    for (const [label, value] of [
      ["金币", module.onAcquire.gold],
      ["经验", module.onAcquire.experience],
      ["结晶", module.onAcquire.crystals],
      ["攻击力", module.onAcquire.damage],
      ["生命上限", module.onAcquire.maxHealth],
      ["魔力上限", module.onAcquire.maxMana],
      ["护甲", module.onAcquire.armor],
      ["攻击速度", module.onAcquire.attackSpeedBonus],
      ["技能伤害", module.onAcquire.abilityDamageBonus],
      ["最大魔力比例恢复", module.onAcquire.manaRegenMaxRatio],
      ["本级总经验比例", module.onAcquire.currentLevelExperienceRatio],
      ["直接等级", module.onAcquire.grantLevels],
      ["本源点", module.onAcquire.talentPoints],
    ] as const) {
      if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
        errors.push(`单次效果的${label}数值无效。`);
      }
    }
    if (
      module.onAcquire.grantLevels !== undefined &&
      (!Number.isInteger(module.onAcquire.grantLevels) ||
        module.onAcquire.grantLevels < 1)
    ) {
      errors.push("单次效果的直接等级必须为正整数。");
    }
    if (
      module.onAcquire.currentLevelExperienceRatio !== undefined &&
      module.onAcquire.currentLevelExperienceRatio <= 0
    ) {
      errors.push("单次效果的本级总经验比例必须大于0。");
    }
    if (
      module.onAcquire.repeatableHiddenEffectId !== undefined &&
      !ID_PATTERN.test(module.onAcquire.repeatableHiddenEffectId)
    ) {
      errors.push("单次效果的可重复隐性效果ID无效。");
    }
  }
  if (module.category === "one_shot" && !module.onAcquire) {
    errors.push("单次技能必须配置获得时效果。");
  }
  if (module.healing) {
    if (
      !["self", "team", "core", "lowest_ally"].includes(module.healing.target) ||
      !nonNegative(module.healing.amount) ||
      !positive(module.healing.intervalMs)
    ) {
      errors.push("治疗效果的目标、数值或频次无效。");
    }
    validateOptionalNumbers(module.healing, errors, "治疗效果");
  }
  for (const mechanic of module.mechanics ?? []) {
    validateOptionalNumbers(mechanic, errors, `技能机制.${mechanic.kind}`);
    if (
      mechanic.kind === "periodic_target_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range) ||
        !positive(mechanic.damage) ||
        !["random", "heavy"].includes(mechanic.target) ||
        (mechanic.slotIndependent !== undefined &&
          typeof mechanic.slotIndependent !== "boolean"))
    ) {
      errors.push("周期伤害机制必须使用项目允许的攻击间隔和范围档位。");
    }
    if (
      mechanic.kind === "enemy_conversion" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range))
    ) {
      errors.push("转化机制必须使用项目允许的攻击间隔和范围档位。");
    }
    if (
      mechanic.kind === "black_hole" &&
      (!isAllowedAttackIntervalMs(mechanic.spawnIntervalMs) ||
        !isAllowedAttackIntervalMs(mechanic.tickIntervalMs) ||
        !isAllowedSkillRange(mechanic.castRange) ||
        !isAllowedSkillRange(mechanic.diameter))
    ) {
      errors.push("黑洞机制必须使用项目允许的攻击间隔和范围档位。");
    }
    if (
      mechanic.kind === "basic_attack_cannon" &&
      !isAllowedSkillRange(mechanic.baseDiameter)
    ) {
      errors.push("炮击机制的基础爆炸直径必须使用项目允许的范围档位。");
    }
    if (
      mechanic.kind === "pasture_effect_amplifier" &&
      (!isFiniteNumber(mechanic.effectMultiplier) ||
        mechanic.effectMultiplier < 1)
    ) {
      errors.push("牧灵效果增幅倍率必须是大于等于1的有限数字。");
    }
    if (mechanic.kind === "undead_summon") {
      const unitExists = sharedUnits
        ? sharedUnits.some((unit) => unit.id === mechanic.unitId)
        : (() => {
            try {
              getUndeadUnit(mechanic.unitId);
              return true;
            } catch {
              return false;
            }
          })();
      if (!unitExists) {
        errors.push(`亡灵召唤引用了未知单位：${mechanic.unitId}`);
      }
      if (
        !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        mechanic.manaCost < 0
      ) {
        errors.push("亡灵召唤必须使用有效的间隔和非负魔力消耗。");
      }
    }
    if (
      mechanic.kind === "lich_form" &&
      (mechanic.undeadHealthDamageMultiplier < 1 ||
        mechanic.undeadSummonIntervalMultiplier <= 0 ||
        mechanic.undeadSummonIntervalMultiplier > 1)
    ) {
      errors.push("巫妖形态的亡灵强化或召唤频率倍率无效。");
    }
    if (
      mechanic.kind === "guard_periodic_area_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.radius) ||
        mechanic.damage <= 0)
    ) {
      errors.push("护卫范围伤害必须使用有效的间隔、半径和伤害。");
    }
    if (
      mechanic.kind === "earth_spirit_summon" &&
      (!isAllowedSkillRange(mechanic.spawnRadius) ||
        !isAllowedSkillRange(mechanic.alertRange) ||
        mechanic.returnDelayMs <= 0)
    ) {
      errors.push("土灵召唤必须使用有效的生成半径、警戒范围和回归等待时间。");
    }
    if (
      mechanic.kind === "follower_periodic_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range) ||
        mechanic.damage <= 0 ||
        !Number.isInteger(mechanic.hits) ||
        mechanic.hits < 1 ||
        (mechanic.consumeEmptyTick !== undefined &&
          typeof mechanic.consumeEmptyTick !== "boolean"))
    ) {
      errors.push("随从攻击必须使用有效的间隔、范围、伤害和命中次数。");
    }
    if (
      mechanic.kind === "follower_periodic_area_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.radius) ||
        !positive(mechanic.damage))
    ) {
      errors.push("随从范围攻击必须使用有效的间隔、半径和伤害。");
    }
    if (
      mechanic.kind === "periodic_health_extremes_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range) ||
        !positive(mechanic.highestDamage) ||
        !positive(mechanic.lowestDamage))
    ) {
      errors.push("生命极值攻击必须使用有效的间隔、范围和伤害。");
    }
    if (
      mechanic.kind === "basic_attack_chain" &&
      (!Number.isInteger(mechanic.targets) ||
        mechanic.targets < 2 ||
        !positive(mechanic.initialDamage) ||
        mechanic.damageLossPerBounce < 0 ||
        !isAllowedSkillRange(mechanic.bounceRange))
    ) {
      errors.push("普攻弹射必须使用有效的目标数、伤害和弹射范围。");
    }
    if (
      mechanic.kind === "movement_damage_field" &&
      (!positive(mechanic.distancePerField) ||
        !isAllowedSkillRange(mechanic.radius) ||
        !isAllowedAttackIntervalMs(mechanic.tickIntervalMs) ||
        !positive(mechanic.durationMs) ||
        !positive(mechanic.damage))
    ) {
      errors.push("移动伤害场必须使用有效的距离、半径、间隔和伤害。");
    }
    if (
      mechanic.kind === "guard_charge" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.acquireRange) ||
        !isAllowedSkillRange(mechanic.pathWidth) ||
        !positive(mechanic.damage))
    ) {
      errors.push("护卫冲锋必须使用有效的间隔、范围、路径宽度和伤害。");
    }
    if (
      mechanic.kind === "periodic_self_area_damage" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.radius) ||
        mechanic.damage <= 0 ||
        (mechanic.healOnPositiveHit !== undefined &&
          !positive(mechanic.healOnPositiveHit)) ||
        (mechanic.slotIndependent !== undefined &&
          typeof mechanic.slotIndependent !== "boolean"))
    ) {
      errors.push("自身范围伤害必须使用有效的间隔、半径和伤害。");
    }
    if (
      mechanic.kind === "periodic_normal_execute_reward" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range) ||
        mechanic.rewardMultiplier < 1)
    ) {
      errors.push("普通敌人处决必须使用有效的间隔、范围和资源倍率。");
    }
    if (
      mechanic.kind === "periodic_forced_aggro" &&
      (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
        !isAllowedSkillRange(mechanic.range) ||
        mechanic.slowRatio <= 0 ||
        mechanic.slowRatio > 1)
    ) {
      errors.push("强制仇恨必须使用有效的间隔、范围和减速比例。");
    }
    if (
      mechanic.kind === "ability_kill_growth" &&
      (!Number.isInteger(mechanic.killsPerSettlement) ||
        mechanic.killsPerSettlement < 1 ||
        mechanic.maxHealthAdd <= 0 ||
        mechanic.damageAdd <= 0)
    ) {
      errors.push("有效击杀成长必须使用正整数门槛及正数成长值。");
    }
  }
  if (
    module.special !== undefined &&
    !SPECIAL_BEHAVIORS.includes(module.special)
  ) {
    errors.push("特殊机制ID无效。");
  }
  for (const entry of getModuleSkillRangeEntries(module)) {
    if (!isAllowedSkillRange(entry.value)) {
      errors.push(
        `${entry.label}必须使用项目允许的范围档位（小于200时为5的倍数，大于等于200时为10的倍数）。`,
      );
    }
  }
  for (const [label, value] of [
    ["固定升格目标", module.evolutionTo],
    ["固定升格来源", module.evolutionFrom],
  ] as const) {
    if (value !== undefined && !ID_PATTERN.test(value)) {
      errors.push(`${label}不是有效技能ID。`);
    }
  }
  return errors;
}

function validateOptionalNumbers(
  value: object,
  errors: string[],
  label: string,
): void {
  for (const [key, candidate] of Object.entries(value)) {
    if (
      typeof candidate === "number" &&
      !Number.isFinite(candidate)
    ) {
      errors.push(`${label}.${key} 必须是有限数字。`);
    }
  }
}

function validateNumericRecord(value: unknown): boolean {
  const object = asRecord(value);
  return Boolean(
    object &&
      Object.values(object).every(
        (candidate) =>
          typeof candidate === "number" && Number.isFinite(candidate),
      ),
  );
}

function positive(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

function nonNegative(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
